import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack,
  Delete,
  Edit as EditIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import LogActionModal from "./components/LogActionModal";
import { useNavigate, useParams } from "react-router-dom";
import { DailyLog } from "./types";
import { fixedIncomeApi } from "./apiService";
import { Loading } from "../../components/Loading";
import { formatCurrency, formatDate, formatTime } from "../../utils/utils";
import FixedExpense from "./components/FixedExpense";
import IncomeDetails from "./components/IncomeDetails";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import {
  bookingDetailsCellStyles,
  bookingDetailsHeaderStyles,
  deletedTableStyles,
  tableCellStyles,
  tableHeaderStyles,
} from "../../utils/styles";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function DailyLogsTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const FixedIncomeDailyLogs = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLog, setEditingLog] = useState<DailyLog | null>(null);
  const [formData, setFormData] = useState<Partial<DailyLog>>({
    tripStartOdometer: 0,
    tripEndOdometer: 0,
    driverName: "",
    tripStartTime: "",
    tripEndTime: "",
    numberOfTrips: 1,
    startPoint: "",
    endPoint: "",
  });
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const [bookingDetails, setBookingDetails] = useState<any>(null);

  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [softDeletedLogs, setSoftDeletedLogs] = useState<DailyLog[]>([]);
  const [showChangeLogs, setShowChangeLogs] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [logToDelete, setLogToDelete] = useState<DailyLog | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setLogToDelete(null);
  };

  const handleDeleteLog = async (log: DailyLog) => {
    if (log) {
      const response = await fixedIncomeApi.softDeleteDailyLog(id!, log);
      if (response) {
        getDailyLogs();
        setDeleteModalOpen(false);
      }
    }
  };

  const handleOpenDialog = (log?: DailyLog) => {
    if (log) {
      setEditingLog(log);
      setFormData(log);
    } else {
      setEditingLog(null);
      setFormData({
        tripStartOdometer: 0,
        tripEndOdometer: 0,
        driverName: "",
        tripStartTime: "",
        tripEndTime: "",
        numberOfTrips: 1,
        startPoint: "",
        endPoint: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLog(null);
    setFormData({
      tripStartOdometer: 0,
      tripEndOdometer: 0,
      driverName: "",
      tripStartTime: "",
      tripEndTime: "",
      numberOfTrips: 1,
      startPoint: "",
      endPoint: "",
    });
  };

  const handleBack = () => {
    navigate("/fixed-income");
  };

  const getDailyLogs = async () => {
    setIsLoading(true);
    try {
      const responses = await Promise.all([
        fixedIncomeApi.getDailyLogs(id!),
        fixedIncomeApi.getSoftDeletedDailyLogs(id!),
      ]);

      const [bookingData, softDeletedLogs] = responses;
      const { dailyLogs, bookingDetails } = bookingData || {};

      // normal logs
      setBookingDetails(bookingDetails);
      setLogs(dailyLogs || []);

      // deleted/changed logs
      setSoftDeletedLogs(softDeletedLogs || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDailyLogs();
  }, [id]);

  if (isLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  const LogsTable = ({
    logs,
    isDeleted,
  }: {
    logs: DailyLog[];
    isDeleted: boolean;
  }) => {
    const baseTableHeaderLabels = [
      "Sr No.",
      "Trip Odometer",
      "Driver Name",
      "Trip Time",
      "No. of Trips",
      "From",
      "To",
      "Date of Entry",
      "Remarks",
    ];

    const tableHeaderLabels = isDeleted
      ? baseTableHeaderLabels
      : [...baseTableHeaderLabels, "Actions"];
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {tableHeaderLabels.map((label, index) => (
                <TableCell
                  sx={isDeleted ? deletedTableStyles : tableHeaderStyles}
                  key={index}
                  align={label === "Actions" ? "center" : "left"}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, index) => (
                <TableRow key={log.id} hover>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {log.tripStartOdometer} km (Start)
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {log.tripEndOdometer} km (End)
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.driverName}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {formatTime(log.tripStartTime)} (Start)
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {formatTime(log.tripEndTime)} (End)
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.numberOfTrips || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.startPoint || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.endPoint || "-"}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {formatDate(log.dateOfEntry)}
                  </TableCell>
                  <TableCell
                    sx={isDeleted ? deletedTableStyles : tableCellStyles}
                  >
                    {log.remarks || "-"}
                  </TableCell>
                  {!isDeleted && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(log)}
                        color="primary"
                        title="Edit Log"
                      >
                        <EditIcon sx={{ fontSize: "0.8rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setLogToDelete(log);
                          setDeleteModalOpen(true);
                        }}
                        color="error"
                        title="Delete Log"
                      >
                        <Delete sx={{ fontSize: "0.8rem" }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2, fontSize: "0.75rem" }}
        >
          Back to Fixed Income
        </Button>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: "1.25rem" }}
          gutterBottom
        >
          Daily Logs
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: "0.75rem" }}
          gutterBottom
        >
          View and manage daily trip logs.
        </Typography>

        {/* Rental Details Section */}
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mb: 2, fontSize: "1rem" }}
          >
            Contract Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Client Name
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {bookingDetails?.clientName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Vehicle Number
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {bookingDetails?.vehicleNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Contract Dates
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {formatDate(bookingDetails?.journeyStartDateTime)} to{" "}
                {formatDate(bookingDetails?.journeyEndDateTime)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Billing Frequency
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {bookingDetails?.billingFrequency !== "CUSTOM"
                  ? bookingDetails?.billingFrequency
                  : `${bookingDetails?.customNoOfBillingDays} days`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Billing Start Date
              </Typography>
              <Typography variant="body1" sx={bookingDetailsCellStyles}>
                {formatDate(bookingDetails?.billingStartDate)}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: 1, fontSize: "1rem" }}
          >
            Income Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={bookingDetailsHeaderStyles}
              >
                Income Type
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textTransform: "capitalize",
                  ...bookingDetailsCellStyles,
                }}
              >
                {bookingDetails?.incomeType}
              </Typography>
            </Grid>
            {bookingDetails?.incomeType === "fixed" ? (
              <Grid item xs={12} sm={6} md={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={bookingDetailsHeaderStyles}
                >
                  Fixed Amount
                </Typography>
                <Typography variant="body1" sx={bookingDetailsCellStyles}>
                  {formatCurrency(bookingDetails?.fixedAmount)}
                </Typography>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Rate
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    {formatCurrency(bookingDetails?.amount)}
                    {bookingDetails?.incomeType === "perkm" ? "/km" : "/ton"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Over Limit Rate
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    {formatCurrency(bookingDetails?.overLimitAmount)}
                    {bookingDetails?.incomeType === "perkm" ? "/km" : "/ton"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={bookingDetailsHeaderStyles}
                  >
                    Over Limit Kms
                  </Typography>
                  <Typography variant="body1" sx={bookingDetailsCellStyles}>
                    {bookingDetails?.overLimitKms}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Logs" {...a11yProps(0)} />
          {/* <Tab label="Income" {...a11yProps(1)} /> */}
          <Tab label="Expenses" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <DailyLogsTabPanel value={tabValue} index={0}>
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ height: "30px", fontSize: "0.75rem" }}
          >
            Add Log
          </Button>
        </Grid>
        <LogsTable logs={logs} isDeleted={false} />

        {softDeletedLogs.length > 0 && (
          <Grid container sx={{ my: 2 }}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "gray",
                  fontSize: "0.75rem",
                  mb: 2,
                }}
                onClick={() => {
                  setShowChangeLogs(!showChangeLogs);
                }}
              >
                Changed Logs
                {showChangeLogs ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </Typography>
              {showChangeLogs && (
                <LogsTable logs={softDeletedLogs} isDeleted={true} />
              )}
            </Grid>
          </Grid>
        )}
      </DailyLogsTabPanel>
      {/* <DailyLogsTabPanel value={tabValue} index={1}>
        <Paper>
          <IncomeDetails
            onNext={() => {}}
            fixedJourneyId={id}
            incomeData={null}
            setIncomeSummary={() => {}}
            setIncomeData={() => {}}
          />
        </Paper>
      </DailyLogsTabPanel> */}
      <DailyLogsTabPanel value={tabValue} index={1}>
        <FixedExpense
          selectedVehicle={{
            vehicleNumber: bookingDetails?.vehicleNumber,
            vehicleId: bookingDetails?.vehicleId,
          }}
          fixedJourneyId={id}
          setExpenseData={() => {}}
        />
      </DailyLogsTabPanel>

      <LogActionModal
        open={openDialog}
        onClose={handleCloseDialog}
        editingLog={editingLog}
        formData={formData}
        fixedJourneyId={id!}
        getDailyLogs={getDailyLogs}
      />

      <DeleteConfirmationDialog
        deleteModalOpen={deleteModalOpen}
        handleNo={closeDeleteModal}
        handleYes={handleDeleteLog}
        itemToDelete={logToDelete}
        title="Delete Log"
        message="Are you sure you want to delete this log?"
      />
    </Container>
  );
};

export default FixedIncomeDailyLogs;
